/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import InputField from "components/fields/InputField";
import PayoutSearchReceiverListModal from "./PayoutSearchReceiverListModal";
import PayoutSearchReceiverAccountListModal from "./PayoutSearchReceiverAccountListModal";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";

const InputStyle =
  "daterange-picker font-medium text-navy-700 h-10 mt-2 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)", // Default border color
      borderRadius: "2px",
      _hover: {
        background: "gray", // Background color on hover
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray", // Background color for dates in the selected range
      color: "white", // Text color for dates in the selected range
    },
    selectedBtnProps: {
      background: "black", // Background color for selected date
      color: "white", // Text color for selected date
    },
    todayBtnProps: {
      // color: "#422afb", // Background color for today's date
      // fontWeight: "bold",
    },
  },
  inputProps: {
    // minWidth: "230px",
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)", // Apply custom box shadow
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9", // Border color for the calendar wrapper
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

const PayoutSearch: React.FC<{
  showFilters: boolean;
  userRole: any;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  setSelectedDates: any;
  selectedDates: any;
  handleSearchSubmit: () => void;
  handleSearchClear: () => void;
  assetList: any;
}> = ({
  showFilters,
  userRole,
  isFilterLoading,
  filters,
  setFilters,
  setSelectedDates,
  selectedDates,
  handleSearchSubmit,
  handleSearchClear,
  assetList,
}) => {
  // Modal states for merchant and subaccount selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);

  // Modal states for receiver and receiver account selection
  const [isReceiverModalOpen, setIsReceiverModalOpen] = useState<boolean>(false);
  const [isReceiverAccountModalOpen, setIsReceiverAccountModalOpen] = useState<boolean>(false);
  const [selectedReceiver, setSelectedReceiver] = useState<any>(null);
  const [selectedReceiverAccount, setSelectedReceiverAccount] = useState<any>(null);

  const formatLabel = (label: any) => {
    let modifiedLabel = label.split("_").join(" ");
    switch (label) {
      case "bsb":
        modifiedLabel = "BSB";
        break;
      case "bic":
        modifiedLabel = "BIC";
        break;
      case "iban":
        modifiedLabel = "IBAN";
        break;
    }

    return modifiedLabel;
  };

  // Format receiver account display: Asset / Method / Account Details
  const formatReceiverAccountDisplay = (account: any) => {
    if (!account) return "All";

    const details = account.method_details || {};
    const detailParts = Object.entries(details)
      .filter(([, value]) => value !== "")
      .map(([key, value]) => `${formatLabel(key)}: ${value}`);

    const parts = [account.asset, account.method_name];
    if (detailParts.length > 0) {
      parts.push(detailParts.join(" / "));
    }

    return parts.join(" / ");
  };

  const handleInputChange = async (event: any) => {
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setSelectedMerchant(merchantData);
    // Clear dependent selections
    setSelectedSubaccount(null);
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("merchantIdToFilterPayout", merchantData.id);
    localStorage.setItem("subaccountIdToFilterPayout", "");
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("merchantIdToFilterPayout", "");
    localStorage.setItem("subaccountIdToFilterPayout", "");
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setSelectedSubaccount(subaccountData);
    // Clear dependent selections
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("subaccountIdToFilterPayout", subaccountData.id);
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
    setFilters({ ...filters, subaccount_id: subaccountData.id });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setSelectedSubaccount(null);
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("subaccountIdToFilterPayout", "");
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
    setFilters({ ...filters, subaccount_id: "" });
  };

  // Handler for receiver selection from modal
  const handleReceiverSelect = (receiverData: any) => {
    setSelectedReceiver(receiverData);
    setSelectedReceiverAccount(null);
    localStorage.setItem("receiverIdToFilterPayout", receiverData.id);
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
  };

  // Handler for receiver account selection from modal
  const handleReceiverAccountSelect = (accountData: any) => {
    setSelectedReceiverAccount(accountData);
    localStorage.setItem("receiverAccountIdToFilterPayout", accountData.id);
  };

  // Clear receiver selection
  const handleClearReceiver = () => {
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
  };

  // Clear receiver account selection
  const handleClearReceiverAccount = () => {
    setSelectedReceiverAccount(null);
    localStorage.setItem("receiverAccountIdToFilterPayout", "");
  };

  const handleClear = () => {
    // Clear merchant and subaccount selections
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    localStorage.setItem("merchantIdToFilterPayout", "");
    localStorage.setItem("subaccountIdToFilterPayout", "");

    // Clear receiver and receiver account selections
    setSelectedReceiver(null);
    setSelectedReceiverAccount(null);
    localStorage.setItem("receiverIdToFilterPayout", "");
    localStorage.setItem("receiverAccountIdToFilterPayout", "");

    handleSearchClear();
  };

  // If not showing filters, render nothing (button lives outside in the toolbar)
  if (!showFilters) return null;

  return (
    <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
        {userRole === "superadmin" && (
          <>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Merchant
              </label>
              <Tooltip
                title={selectedMerchant ? selectedMerchant.name : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && setIsMerchantModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedMerchant ? selectedMerchant.name : "All"}
                  </span>
                  {selectedMerchant && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Subaccount
              </label>
              <Tooltip
                title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && selectedMerchant && setIsSubaccountModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading || !selectedMerchant
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                  </span>
                  {selectedSubaccount && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Receiver
              </label>
              <Tooltip
                title={selectedReceiver ? `${selectedReceiver.first_name} ${selectedReceiver.last_name} (${selectedReceiver.email})` : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && setIsReceiverModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedReceiver ? `${selectedReceiver.first_name} ${selectedReceiver.last_name} (${selectedReceiver.email})` : "All"}
                  </span>
                  {selectedReceiver && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearReceiver(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Receiver Account
              </label>
              <Tooltip
                title={formatReceiverAccountDisplay(selectedReceiverAccount)}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && selectedReceiver && setIsReceiverAccountModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading || !selectedReceiver
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {formatReceiverAccountDisplay(selectedReceiverAccount)}
                  </span>
                  {selectedReceiverAccount && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearReceiverAccount(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Asset
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="asset"
                value={filters.asset}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">{assetList ? "All" : "Retrieving..."}</option>
                {assetList &&
                  assetList.filter((item: any) => !item.crypto).length > 0 &&
                  assetList.filter((item: any) => !item.crypto) && (
                    <optgroup label="Fiat">
                      {assetList &&
                        assetList
                          .filter((item: any) => !item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
                {assetList &&
                  assetList.filter((item: any) => item.crypto).length > 0 &&
                  assetList.filter((item: any) => item.crypto) && (
                    <optgroup label="Crypto">
                      {assetList &&
                        assetList
                          .filter((item: any) => item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
              </Select>
            </div>
            <div className="tracking-tight text-gray-900 md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Date
              </label>
              <div className="search-picker-custom">
                <RangeDatepicker
                  disabled={isFilterLoading}
                  selectedDates={selectedDates}
                  onDateChange={setSelectedDates}
                  propsConfigs={dateRangePropsConfigs}
                  configs={{
                    dateFormat: "MMM d, yyyy",
                  }}
                  maxDate={new Date()}
                />
              </div>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Field
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="field"
                value={filters.field}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none sm:text-sm"
              >
                <option value="">Select Field</option>
                <option value="id">ID</option>
                <option value="reference">Reference</option>
                <option value="reference_tracking">Ref. Tracking</option>
              </Select>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Keyword
              </label>
              <div className="search-picker-custom">
                <InputField
                  disabled={isFilterLoading}
                  variant="auth"
                  extra="shadow-sm rounded-md"
                  label=""
                  placeholder=""
                  id="keyword"
                  type="text"
                  autoComplete="off"
                  value={filters.keyword}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Status
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="status"
                value={filters.status}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">All</option>
                <option value="0">Awaiting</option>
                <option value="1">Pending</option>
                <option value="2">Unassessed</option>
                <option value="3">In Progress</option>
                <option value="4">Completed</option>
                <option value="5">Cancelled</option>
                <option value="6">Failed</option>
                <option value="7">Completed Pending KYC</option>
              </Select>
            </div>
          </>
        )}
        {userRole !== "superadmin" && (
          <>
            <div className="tracking-tight text-gray-900 md:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Date
              </label>
              <div className="search-picker-custom">
                <RangeDatepicker
                  disabled={isFilterLoading}
                  selectedDates={selectedDates}
                  onDateChange={setSelectedDates}
                  propsConfigs={dateRangePropsConfigs}
                  configs={{
                    dateFormat: "MMM d, yyyy",
                  }}
                  maxDate={new Date()}
                />
              </div>
            </div>
            {userRole === "merchant" && (
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Subaccount
                </label>
                <Tooltip
                  title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && setIsSubaccountModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isFilterLoading
                        ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                        : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                    </span>
                    {selectedSubaccount && !isFilterLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
            )}
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Receiver
              </label>
              <Tooltip
                title={selectedReceiver ? `${selectedReceiver.first_name} ${selectedReceiver.last_name} (${selectedReceiver.email})` : "All"}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && setIsReceiverModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {selectedReceiver ? `${selectedReceiver.first_name} ${selectedReceiver.last_name} (${selectedReceiver.email})` : "All"}
                  </span>
                  {selectedReceiver && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearReceiver(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Receiver Account
              </label>
              <Tooltip
                title={formatReceiverAccountDisplay(selectedReceiverAccount)}
                arrow
                placement="top"
              >
                <div
                  onClick={() => !isFilterLoading && selectedReceiver && setIsReceiverAccountModalOpen(true)}
                  className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                    isFilterLoading || !selectedReceiver
                      ? "cursor-not-allowed bg-disabledBg text-gray-700 font-medium py-[11px]"
                      : "cursor-pointer bg-white text-navy-700 hover:bg-gray-50 border border-gray-300 shadow-sm font-medium py-2.5"
                  }`}
                >
                  <span className="truncate">
                    {formatReceiverAccountDisplay(selectedReceiverAccount)}
                  </span>
                  {selectedReceiverAccount && !isFilterLoading && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleClearReceiverAccount(); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                    >
                      &times;
                    </button>
                  )}
                </div>
              </Tooltip>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Asset
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="asset"
                value={filters.asset}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">{assetList ? "All" : "Retrieving..."}</option>
                {assetList &&
                  assetList.filter((item: any) => !item.crypto).length > 0 &&
                  assetList.filter((item: any) => !item.crypto) && (
                    <optgroup label="Fiat">
                      {assetList &&
                        assetList
                          .filter((item: any) => !item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
                {assetList &&
                  assetList.filter((item: any) => item.crypto).length > 0 &&
                  assetList.filter((item: any) => item.crypto) && (
                    <optgroup label="Crypto">
                      {assetList &&
                        assetList
                          .filter((item: any) => item.crypto)
                          .map((asset: any) => (
                            <option key={asset.name} value={asset.name}>
                              {asset.name}
                            </option>
                          ))}
                    </optgroup>
                  )}
              </Select>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Field
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="field"
                value={filters.field}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none sm:text-sm"
              >
                <option value="">Select Field</option>
                <option value="id">ID</option>
                <option value="reference">Reference</option>
                <option value="reference_tracking">Ref. Tracking</option>
              </Select>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Keyword
              </label>
              <div className="search-picker-custom">
                <InputField
                  disabled={isFilterLoading}
                  variant="auth"
                  extra="shadow-sm rounded-md"
                  label=""
                  placeholder=""
                  id="keyword"
                  type="text"
                  autoComplete="off"
                  value={filters.keyword}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="tracking-tight text-gray-900">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                Status
              </label>
              <Select
                isDisabled={isFilterLoading}
                id="status"
                value={filters.status}
                onChange={handleInputChange}
                className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
              >
                <option value="">All</option>
                <option value="0">Awaiting</option>
                <option value="1">Pending</option>
                <option value="2">Unassessed</option>
                <option value="3">In Progress</option>
                <option value="4">Completed</option>
                <option value="5">Cancelled</option>
                <option value="6">Failed</option>
                <option value="7">Completed Pending KYC</option>
              </Select>
            </div>
            <div className={`mt-4 flex items-center justify-end gap-2 ${userRole === "merchant" ? "md:col-span-1" : "md:col-span-2"}`}>
              <Button
                isDisabled={isFilterLoading}
                onClick={handleClear}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                Clear
              </Button>

              <Button
                isDisabled={isFilterLoading}
                onClick={handleSearchSubmit}
                className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <span className="relative z-10 tracking-tight">
                  Search Payouts
                </span>
                <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
              </Button>
            </div>
          </>
        )}
      </div>
      {userRole === "superadmin" && (
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            isDisabled={isFilterLoading}
            onClick={() => handleClear()}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
          >
            Clear
          </Button>
          <Button
            isDisabled={isFilterLoading}
            onClick={handleSearchSubmit}
            className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="relative z-10 tracking-tight">Search Payouts</span>
            <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
          </Button>
        </div>
      )}

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && (selectedMerchant || userRole === "merchant") && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant?.id || localStorage.getItem("merchantId") || ""}
        />
      )}

      {/* Receiver Selection Modal */}
      {isReceiverModalOpen && (
        <PayoutSearchReceiverListModal
          isOpen={isReceiverModalOpen}
          onClose={() => setIsReceiverModalOpen(false)}
          onSelect={handleReceiverSelect}
          subaccountId={selectedSubaccount?.id}
        />
      )}

      {/* Receiver Account Selection Modal */}
      {isReceiverAccountModalOpen && selectedReceiver && (
        <PayoutSearchReceiverAccountListModal
          isOpen={isReceiverAccountModalOpen}
          onClose={() => setIsReceiverAccountModalOpen(false)}
          onSelect={handleReceiverAccountSelect}
          receiverId={selectedReceiver.id}
        />
      )}
    </Box>
  );
};

export default PayoutSearch;
